import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { ActionIcon, Button, Flex, Tooltip } from "@mantine/core";
import {
  IconCheck,
  IconEyeCheck,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import {
  CellValueChangedEvent,
  ColDef,
  IRichCellEditorParams,
  IRowNode,
} from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { MRT_PaginationState } from "mantine-react-table";
import React, { useCallback, useEffect, useState } from "react";

import { SerialOnHand, TblInventoryChildSerialOnhandCommands } from "../../WarehouseInwardNew/pages/warehouseInWardSerialChildren/warehouseInterFace";
import { TblInventoryChildSerialOnhandCommand } from "../../../../model/TblInventorySerialOnHand";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { baseTheme, paginationPageSize, paginationPageSizeSelector } from "../../../../_base/_const/_constVar";

interface Props {
  WarehouseInWardSerialChildren: SerialOnHand;
  handleDeleteRow?: (
    index: string,
    data: CellValueChangedEvent<TblInventoryChildSerialOnhandCommand>
  ) => void;
  handleAddPlus?: (
    value: string,
    data: TblInventoryChildSerialOnhandCommands[]
  ) => void;
  action?: string;
  type?: string;
  sourceType?: string;
}

export const WarehouseOutWardSerialChildrenV2: React.FC<Props> = ({
  WarehouseInWardSerialChildren,
  handleDeleteRow,
  handleAddPlus,
  action,
  type,
  sourceType,
}) => {
  const [data, setData] = useState<TblInventoryChildSerialOnhandCommands[]>([]);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  //table state
  const [selectedRows, setSelectedRows] = useState<
    TblInventoryChildSerialOnhandCommand[]
  >([]);

  const gridRef =
    React.useRef<AgGridReact<TblInventoryChildSerialOnhandCommand>>(null);

  const handleAddRow = () => {
    const newRow = {
      parentSerial: WarehouseInWardSerialChildren?.serial,
      serial: "",
      itemCode: "",
      itemName: "",
      uomCode: "",
      quantity: 0,
    };
    setData((prev) => [...prev, newRow]);
    handleAddPlus?.(WarehouseInWardSerialChildren?.serial, data);
  };

  useEffect(() => {
    if (data.length > 0)
      handleAddPlus?.(WarehouseInWardSerialChildren?.serial, data);
  }, [data]);

  const formatItemCode = (value: string): string => {
    return value
      .normalize("NFD") // Loại bỏ dấu tiếng Việt
      .replace(/[\u0300-\u036f]/g, "") // Xóa dấu
      .replace(/[^a-zA-Z0-9]/g, "") // Xóa ký tự đặc biệt, giữ lại chữ và số
      .toUpperCase(); // Chuyển thành chữ in hoa
  };

  const defaultColDef = React.useMemo<
    ColDef<TblInventoryChildSerialOnhandCommand>
  >(() => {
    return {
      editable: true,
      filter: false,
      floatingFilter: false,
      minWidth: 100,
    };
  }, []);

  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent<TblInventoryChildSerialOnhandCommand>) => {},
    []
  );

  const containerStyle = React.useMemo(
    () => ({ width: "100%", height: height + 20, marginTop: "10px" }),
    [height]
  );

  const getRowClass = (params: any) => {
    if (!params.data) {
      return "row-error";
    }
    return "";
  };

  const handleDeleteRowColumn = (index: number) => {
    setData((prev) => prev.filter((_, i) => i !== index));
  };

  const checkAddRow = () => {
    const rowsWithErrors: any[] = [];
    const validateCell = (
      node: IRowNode<TblInventoryChildSerialOnhandCommand>,
      column: string,
      value: any
    ) => {
      if (!value) {
        rowsWithErrors.push(node);
        gridRef.current?.api.flashCells({
          rowNodes: [node],
          columns: [column],
          flashDuration: 9999999,
        });
      }
    };

    gridRef.current?.api.forEachNode(
      (node: IRowNode<TblInventoryChildSerialOnhandCommand>) => {
        validateCell(node, "serial", node.data?.serial);
        validateCell(node, "itemCode", node.data?.itemCode);
        validateCell(node, "itemName", node.data?.itemName);
        validateCell(node, "quantity", node.data?.quantity);
      }
    );
  };

  const columnDefs = React.useMemo<
    ColDef<TblInventoryChildSerialOnhandCommand>[]
  >(
    () => [
      {
        headerName: "#",
        pinned: "left",
        editable: false,
        minWidth: 80,
        maxWidth: 80,
        valueGetter: (params) => Number(params?.node?.rowIndex) + 1,
      },
      {
        field: "parentSerial",
        headerName: "Serial cha",
        filter: "agTextColumnFilter",
        editable: false,
        minWidth: 150,
      },
      {
        field: "serial",
        headerName: "Serial con",
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        minWidth: 150,
        editable: action === "DETAIL" ? false : true,
        onCellValueChanged: (params) => {
          if (!params.newValue) return;
          let newValue = params.newValue.toUpperCase().replace(/\s/g, "");
          newValue = newValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

          const serials = params.api
            .getRenderedNodes()
            .filter((node) => node.rowIndex !== params?.node?.rowIndex)
            .map((node) => node?.data?.serial);

          if (serials.includes(newValue)) {
            params?.node?.setDataValue("serial", null);
            NotificationExtension.Fails(
              "Serial đã tồn tại! Vui lòng nhập serial khác."
            );
          } else {
            params?.node?.setDataValue("serial", newValue);
          }
        },
      },
      {
        field: "itemCode",
        headerName: "Mã sản phẩm",
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        minWidth: 150,
        editable: action === "DETAIL" ? false : true,

        onCellValueChanged: (params) => {
          if (params.newValue) {
            const formattedValue = formatItemCode(params.newValue);
            if (formattedValue !== params.newValue) {
              params.node?.setDataValue("itemCode", formattedValue);
            }
          }
        },
      },
      {
        field: "itemName",
        headerName: "Tên sản phẩm",
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        minWidth: 400,
        editable: action === "DETAIL" ? false : true,
      },
      {
        field: "uomCode",
        headerName: "Đơn vị tính",
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        minWidth: 50,
        editable: action === "DETAIL" ? false : true,
      },
      {
        field: "quantity",
        headerName: "Số lượng",
        cellEditor: "agNumberCellEditor",
        filter: "agNumberColumnFilter",
        editable: action === "DETAIL" ? false : true,
      },
      {
        field: "warrantyPeriod",
        headerName: "Thời gian bảo hành",
        cellEditor: "agNumberCellEditor",
        filter: "agNumberColumnFilter",
        editable: action === "DETAIL" ? false : true,

        minWidth: 140,
        cellEditorParams: (params: CustomCellRendererProps) => {
          return {
            values: [0, 12],
            min: 0,
            max: 12,
          } as IRichCellEditorParams;
        },
      },
      {
        headerName: "Thao tác",
        editable: false,
        minWidth: 50,
        maxWidth: 100,
        cellRenderer: (params: any) => {
          return (
            <Tooltip label="Xóa">
              <ActionIcon
                color="red"
                onClick={() => {
                  handleDeleteRowColumn(params?.node?.rowIndex);
                  handleDeleteRow?.(params?.data?.serial, params);
                }}
                disabled={
                  !["NHAP_HANG_NCC", "NHAP_KHAC"].includes(sourceType ?? "") ||
                  action === "DETAIL" ||
                  type === "XUAT"
                }
                variant="light"
                aria-label="Settings"
              >
                <IconTrash
                  style={{ width: "70%", height: "70%" }}
                  stroke={1.5}
                />
              </ActionIcon>
            </Tooltip>
          );
        },
      },
    ],
    [action]
  );

  useEffect(() => {
    if (WarehouseInWardSerialChildren?.tblInventoryChildSerialOnhandCommands)
      setData(
        WarehouseInWardSerialChildren?.tblInventoryChildSerialOnhandCommands
      );
  }, [WarehouseInWardSerialChildren]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (250 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  return (
    <>
      {/* <div ref={headerRef}>
        <Flex
          mt={10}
          justify="flex-end"
          align="center"
          direction="row"
          wrap="wrap"
          w={"100%"}
          gap={10}
        >
          <Button
            variant="light"
            bd={`"1px solid ${action === "DETAIL" ? "gray" : "#228be6"}"`}
            color="#228be6"
            className={classesButton.button}
            disabled={
              !["NHAP_HANG_NCC", "NHAP_KHAC"].includes(sourceType ?? "") ||
              action === "DETAIL" ||
              type === "XUAT"
            }
            leftSection={<IconPlus size={14} />}
            onClick={() => {
              handleAddRow();
            }}
          >
            Thêm dòng
          </Button>
          <Button
            variant="light"
            color="green"
            bd="1px solid green"
            leftSection={<IconEyeCheck size={14} />}
            onClick={() => {
              checkAddRow();
            }}
          >
            Kiểm tra
          </Button>
        </Flex>
      </div> */}
      <div className="mt-5" style={{ marginTop: "10px" }}>
        {/* <MantineReactTable table={table} />
         */}
        <div style={containerStyle}>
          <AgGridReact<TblInventoryChildSerialOnhandCommand>
            ref={gridRef}
            cellSelection={true}
            onCellValueChanged={onCellValueChanged}
            rowData={data}
            theme={baseTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            suppressColumnVirtualisation={true}
            suppressRowVirtualisation={true}
            pagination={true}
            localeText={AG_GRID_LOCALE_VN}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            // onSelectionChanged={() =>
            //   setSelectedRows(gridRef.current?.api.getSelectedRows() || [])
            // }
            cacheBlockSize={paginationPageSize}
            maxBlocksInCache={3}
            getRowClass={getRowClass}
            suppressRowHoverHighlight={true}
            columnHoverHighlight={true}
            // rowSelection={{ mode: "multiRow" }}
            autoSizeStrategy={{
              type: "fitCellContents",
            }}
            //  getContextMenuItems={getContextMenuItems}
          />
        </div>
      </div>
    </>
  );
};
